import { ExcelExport } from "@progress/kendo-react-excel-export";
import { GridCellProps } from "@progress/kendo-react-grid";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import GridCustomPage from "ui/grid/GridPage";
import styles from "styles.module.css";
import Filter from "./Filter";
import "./styles.css";
import { PageTypePath } from "utils/pages";
import {
	settlementDetailGridItems,
	SettlementGridColumnItem,
} from "./utilities/GridColumns";
import {
	getSettlementLinesApi,
	getSettlementsApi,
	SettlementFilter,
} from "api/accounting";
import { ICON_NAMES } from "utils/constant";
import CustomDetailedPage from "ui/grid/DetailPage";
import { Settlement } from "interface/accounting";
import SettlementsMobileList from "./SettlementsMobileList";

const pageSize = 60;

export default function Settlements() {
	const { t } = useTranslation();

	const gridRef = useRef<HTMLDivElement>(null);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
		const mobileNextSkip = useRef(0);
	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [mobileData, setMobileData] = useState<Settlement[]>([]);
	const [mobileLoading, setMobileLoading] = useState(false);

	const [filterOptions, setFilterOptions] = useState<SettlementFilter>({
		take: pageSize,
		skip: 0,
		fromDate: null,
		toDate: null,
	});

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const requestMobileData = useCallback((skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getSettlementsApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as Settlement[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setMobileData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setMobileData(incoming);
				} else {
					setMobileData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	}, [filterOptions]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setMobileData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, requestMobileData]);

	const [statusOverrides, setStatusOverrides] = useState<Record<number, number>>({});

	const openPdfByRow = (prop: GridCellProps) => {
		window.open(`/${PageTypePath.Pdf}/${prop.dataItem.reportGuid}`, "_blank");

		if (prop.dataItem.status !== 1) {
			setStatusOverrides((prev) => ({ ...prev, [prop.dataItem.id]: 1 }));
		}

		return;
	};

	const openPdfByItem = (item: Settlement) => {
		window.open(`/${PageTypePath.Pdf}/${item.reportGuid}`, "_blank");

		if (item.status !== 1) {
			setStatusOverrides((prev) => ({ ...prev, [item.id]: 1 }));
		}
	};

	return (
		<div className={`${styles.PageContainer} settlement-page`}>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("Settlements")}</legend>
			</div>

			<div>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
				/>
			</div>
			<div style={{ marginTop: 30 }} ref={gridRef}>
				{isMobileView ? (
					<SettlementsMobileList
						items={mobileData}
						loading={mobileLoading}
						onOpenPdf={openPdfByItem}
						onLoadMore={() => requestMobileData(mobileNextSkip.current, pageSize)}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						gridColumns={SettlementGridColumnItem(statusOverrides)}
						detailPage={(prop) => (
							<CustomDetailedPage
								grids={settlementDetailGridItems}
								dataApi={() => getSettlementLinesApi(prop.dataItem.id)}
							/>
						)}
						filter={filterOptions}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						lastColWidth="90px"
						apiMethod={getSettlementsApi}
						excelProps={{
							fileName: `settlements_${new Date().toISOString()}.xlsx`,
						}}
						lastColBtns={[
							{
								onclick: openPdfByRow,
								iconName: ICON_NAMES.Download,
								alwaysShow: true,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
}
